import {
  coverImageUrl,
  type LocalizedPromotion,
  type MerchantModerationState,
  type MerchantPromotionItem,
  type Promotion as ApiPromotion,
} from '@noor/eats-api';
import { formatDottedDate } from '@noor/lib';
import {
  PROMOTION_MODERATIONS,
  type Promotion,
  type PromotionDetail,
  type PromotionModeration,
} from '../model/schema';

// Mappers from the merchant API's joined promotion rows to the promotions
// table's view model.

/**
 * Whole-percent discount, clamped to 0–100. A `DISCOUNT_TYPE_FIXED` promo
 * stores so'm rather than percent, so the percentage is derived from the two
 * prices instead of read off `discount_value`.
 */
function percentOff(price: number, finalPrice: number): number {
  if (price <= 0 || finalPrice >= price) return 0;
  return Math.min(100, Math.max(0, Math.round(((price - finalPrice) / price) * 100)));
}

/**
 * The promo window as the preformatted range the table renders, or `null` for
 * an open-ended promo (shown as "бессрочно"). Dates are rendered `DD.MM.YYYY`;
 * an unparseable bound is treated as absent.
 */
function toDuration(startsAt?: string, endsAt?: string): string | null {
  const start = formatDay(startsAt);
  const end = formatDay(endsAt);
  if (!start && !end) return null;
  if (start && end) return `${start} - ${end}`;
  return start ?? end ?? null;
}

function formatDay(timestamp: string | undefined): string | undefined {
  if (!timestamp) return undefined;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return undefined;
  return formatDottedDate(date);
}

const PROMOTION_MODERATION_BY_API_STATE = {
  MERCHANT_MODERATION_STATE_DRAFT: 'draft',
  MERCHANT_MODERATION_STATE_ON_MODERATION: 'pending',
  MERCHANT_MODERATION_STATE_APPROVED: 'approved',
  MERCHANT_MODERATION_STATE_REJECTED: 'rejected',
} as const satisfies Record<
  Exclude<MerchantModerationState, 'MERCHANT_MODERATION_STATE_UNSPECIFIED'>,
  PromotionModeration
>;

/** `moderation_state` → the row's verdict; `UNSPECIFIED` (or unknown) → none. */
function toPromotionModeration(
  moderationState: MerchantModerationState | undefined,
): PromotionModeration | undefined {
  if (!moderationState) return undefined;
  const mapped: PromotionModeration | undefined =
    PROMOTION_MODERATION_BY_API_STATE[
      moderationState as keyof typeof PROMOTION_MODERATION_BY_API_STATE
    ];
  return mapped && PROMOTION_MODERATIONS.includes(mapped) ? mapped : undefined;
}

/**
 * Map one joined `product × restaurant × promotion` row to a table row.
 *
 * The promotion id addresses the CAMPAIGN, which a batch promo shares across
 * every product × branch pair it covers — so the product and branch ids are
 * carried too. They are what make a row individually addressable: keying the
 * list, and naming which pair to detach when one row is deleted.
 */
export function toPromotion(item: MerchantPromotionItem): Promotion {
  const { product, restaurant, promotion } = item;
  const price = product.price;
  const finalPrice = product.effective_price > 0 ? product.effective_price : price;
  const moderation = toPromotionModeration(item.moderation_state);
  // The legacy top-level `image` string is gone as of API v1.00.155; a
  // product's photos are the `images` gallery, whose cover is the `main`-typed
  // entry (falling back to the first by position, as elsewhere).
  const imageUrl = coverImageUrl(product.images);

  return {
    id: promotion.id,
    productId: product.id,
    restaurantId: restaurant.id,
    productName: product.name,
    // The campaign's OWN name, so the edit form seeds the right thing before
    // the by-id read lands. Locale-resolved on this shape (the list RPC is a
    // read); the by-id read carries all three translations.
    name: promotion.name,
    ...(imageUrl ? { imageUrl } : {}),
    price,
    discountPercent:
      promotion.discount_type === 'DISCOUNT_TYPE_PERCENTAGE'
        ? promotion.discount_value
        : percentOff(price, finalPrice),
    discountType: promotion.discount_type === 'DISCOUNT_TYPE_FIXED' ? 'fixed' : 'percent',
    finalPrice,
    branchName: restaurant.name,
    discountValue: promotion.discount_value,
    isActive: promotion.is_active,
    ...(moderation ? { moderationState: moderation } : {}),
    ...(item.rejection_reason.trim()
      ? { rejectionReason: item.rejection_reason.trim() }
      : {}),
    ...(promotion.starts_at ? { startsAt: promotion.starts_at } : {}),
    ...(promotion.ends_at ? { endsAt: promotion.ends_at } : {}),
    productIds: promotion.product_ids,
    duration: toDuration(promotion.starts_at, promotion.ends_at),
  };
}

/** Map one plain campaign row from `by-merchant/by-restaurant`. */
export function toPromotionCampaign(promotion: ApiPromotion, branchName = ''): Promotion {
  const discountType =
    promotion.discount_type === 'DISCOUNT_TYPE_FIXED' ? 'fixed' : 'percent';
  const discountValue = promotion.discount_value;

  return {
    id: promotion.id,
    restaurantId: promotion.restaurant_id,
    name: promotion.name,
    productId: promotion.product_ids[0] ?? '',
    productIds: promotion.product_ids,
    productName: '',
    price: 0,
    discountPercent: discountType === 'percent' ? discountValue : 0,
    discountType,
    discountValue,
    finalPrice: discountType === 'fixed' ? discountValue : 0,
    branchName,
    isActive: promotion.is_active,
    ...(promotion.starts_at ? { startsAt: promotion.starts_at } : {}),
    ...(promotion.ends_at ? { endsAt: promotion.ends_at } : {}),
    duration: toDuration(promotion.starts_at, promotion.ends_at),
  };
}

/**
 * Map the by-id read to the edit form's seed.
 *
 * `discount_value` means two different things depending on `discount_type` —
 * a percent rate or a fixed final price in so'm — so it is carried verbatim
 * alongside the type rather than normalized into one of them here.
 *
 * The wire omits zero values, so an absent `starts_at`/`ends_at` is a genuinely
 * open bound (an open-ended promo has neither).
 */
export function toPromotionDetail(promotion: LocalizedPromotion): PromotionDetail {
  return {
    id: promotion.id,
    // Every translation, so the wizard's three name inputs seed from the
    // server's own record rather than echoing one resolved string into all of
    // them. A blank member is a genuine "not translated yet".
    nameTranslations: {
      ru: promotion.name.ru,
      uz: promotion.name.uz ?? '',
      en: promotion.name.en ?? '',
    },
    restaurantId: promotion.restaurant_id,
    productIds: promotion.product_ids,
    discountType: promotion.discount_type === 'DISCOUNT_TYPE_FIXED' ? 'fixed' : 'percent',
    discountValue: promotion.discount_value,
    ...(promotion.starts_at ? { startsAt: promotion.starts_at } : {}),
    ...(promotion.ends_at ? { endsAt: promotion.ends_at } : {}),
  };
}
